import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _, { isArray } from 'lodash';
import { useSelector } from 'react-redux';
import htmlToDraft from 'html-to-draftjs';
import { ContentState, EditorState } from 'draft-js';
import {
  AdmIcon,
  BedAvailableIcon,
  CmsAlertStandardIcon,
  DischgIcon,
  DropDownIcon,
  DropUpIcon,
  MoreIcon,
  OccupiedIcon,
  PatientIcon,
} from '../../../../components/Icon';
import { ReactComponent as Attention } from '../../../../static/images/Attention.svg';
import { BED_STATUS_REMARK_COLOR } from '../../../../comm/constant';
import { bedStatusAPI } from '../../../../apis';
import { alertMsg, ResponseCommCodeEnum } from '../../../../apis/commlogic';
import { i18n } from '../../../../language';
import UpdateWardStatus from '../Popups/UpdateWardStatus';
import {
  calculateRemToPx,
  generateGrid,
  handleBedsFilter,
  handleDeleteEmbedBug,
  isNotEmptyArray,
} from '../../../../utils/newBedStatusUtil';
import useCheckLogin from '../../../../utils/useCheckLogin';
import { useBsMemo } from '../../../../utils/useBsMemo';
import bs_mdro_contact from '../../../../static/images/bs_mdro_contact.png';
import bs_mdro_confirmed from '../../../../static/images/bs_mdro_confirmed.png';
import Text from '../../../../components/Text';
import { bsTransferInStatisticRef } from '../..';
import RevampBedStatusIcon from '../../components/RevampBedStatusIcon';
import { BedStatusIcon } from '../../components';
import { AutoCompleteSelect } from '../../../../components';
import { getArrayRandomKey } from '../../../../utils';

const Cubicle = lazy(() => import('../Cubicle'));

const useStyles = makeStyles(() => ({
  wardName: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    color: '#2e4469',
  },
  moreBtn: {
    width: '2rem',
    height: '2rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '4px',
    margin: '0 0.5rem 0 0.5rem',
  },
  moreIcon: {
    fontSize: '1rem',
    color: '#7d86a9',
  },
  wardStatus: {
    height: '1.5rem',
    padding: '0.125rem 0.5rem',
    borderRadius: '2px',
    backgroundColor: '#979bff',
    // marginLeft: '0.725rem',
    display: 'flex',
    alignItems: 'center',
  },
  wardStatusText: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#fff',
  },
  tipBar: ({ allData }) => ({
    backgroundColor: BED_STATUS_REMARK_COLOR[allData.colour]?.bgColor ?? '#fff',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    padding: '0.25rem 0.5rem',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.25rem',
      height: '100%',
      backgroundColor: BED_STATUS_REMARK_COLOR[allData.colour]?.leftColor ?? '#e4e7eb',
      zIndex: '9',
    },
  }),
  tips: ({ allData }) => ({
    fontFamily: 'NotoSans-Medium',
    color: BED_STATUS_REMARK_COLOR[allData.colour]?.color ?? '#2e4469',
    fontSize: '0.875rem',
  }),
  queuingPatient: {
    width: '100%',
    height: '1.563rem',
    borderRadius: '4px',
    backgroundColor: '#7d86a9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#fff',
  },
  statusText: {
    fontFamily: 'NotoSans-Medium',
    color: '#7d86a9',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  admText: {
    fontFamily: 'NotoSans-Medium',
    color: '#7d86a9',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    writingMode: 'vertical-rl' /* 支持竖排文字（现代浏览器）*/,
    textOrientation: 'mixed',
  },
  statusTextData: {
    fontFamily: 'NotoSans-Medium',
    color: '#2e4469',
    fontSize: '0.875rem',
    whiteSpace: 'break-spaces',
  },
  statusTextOpacity: {
    fontFamily: 'NotoSans-Medium',
    color: '#7d86a9',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  statusBoldText: {
    fontFamily: 'NotoSans-Bold',
    color: '#2e4469',
    fontSize: '1.375rem',
  },
  bedAvailableIcon: {
    fontSize: '1.4rem',
    color: '#fff',
  },
  border: {
    width: '0.063rem',
    minWidth: '0.063rem',
    height: 'auto',
    backgroundColor: '#dddddd',
  },
  letterIcon: {
    width: '1.25rem',
    height: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#7b80ff',
  },
  letterText: {
    fontFamily: 'NotoSans-Medium',
    color: '#fff',
    fontSize: '1rem',
    zoom: '0.7',
  },
  detailBtn: ({ detailOpen }) => ({
    fontFamily: 'NotoSans-Medium',
    color: detailOpen ? '#7d86a9' : '#fff',
    fontSize: '1rem',
    width: '9.875rem',
    minWidth: '9.875rem',
    height: '1.8rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '4px',
    backgroundColor: detailOpen ? '#fff' : '#7d86a9',
    // position: detailOpen ? 'absolute' : 'relative',
  }),
  dropIcon: {
    fontSize: '0.875rem',
    marginLeft: '0.375rem',
  },
  patientIcon: {
    fontSize: '1.6rem',
    width: '1.5rem',
    height: '1.5rem',
  },
  graphic: {
    width: '9.375rem',
    height: '9.375rem',
  },
  addCubicle: {
    transform: 'translate(-6.25rem, -9rem)',
    overflow: 'auto',
  },
  addCubicleText: {
    fontFamily: 'NotoSans-Bold',
    color: '#707090',
    fontSize: '1rem',
  },
  floorRoot: ({ floorMemo }) => {
    return {
      width: floorMemo.width > 0 ? floorMemo.width + floorMemo.left : '100%',
      height: floorMemo.height > 0 ? floorMemo.height + floorMemo.top : '20rem',
      position: 'relative',
    };
  },
  bedsRoot: ({ isCubicleV2 }) => ({
    display: 'inline-flex',
    padding: '0',
    gap: isCubicleV2 ? '0.857rem' : '0.2rem',
    flexWrap: 'wrap',
    marginTop: '0.3rem',
  }),
  mdroIcon: {
    width: '1.75rem',
    height: '1.25rem',
  },
  floorPlanStatusBox: ({ floorPlanWidth, isEmptyFloorPlan }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gridGap: '0.6rem',
    width: floorPlanWidth - (isEmptyFloorPlan ? 20 : 10),
    flexWrap: 'wrap',

    [`@media (max-width: 1386px)`]: {
      justifyContent: 'flex-start',
      marginBottom: '0.5rem',
    },
  }),
}));

const sortRegex = new RegExp(process.env.REACT_APP_BED_STATUS_SORT_REGEX);
const handleSort = (data) => {
  if (!Array.isArray(data)) return [];
  const srotingData = _.sortBy(data, [
    (str) => {
      // 先按数据里面的数字排序
      // 如果数据里面匹配不到数字,确保无数字的字符串排在最后
      const numMatch = str.match(/\d+/);
      return numMatch ? parseInt(numMatch[0], 10) : Infinity;
    },
    (str) => {
      // 再按数据里面的括号里面的内容排序
      // 如果匹配不到内容,返回空字符串
      const regexMatch = str.match(sortRegex);
      return regexMatch ? regexMatch[1] : '';
    },
  ]);
  return srotingData.join(', ');
};

const handleAttentionSort = (data) => {
  return _.sortBy(data, (item) => parseInt(item, 10));
};

const FloorPlan = ({
  allData,
  getFloorPlanListAPI,
  setPopup,
  filterData,
  readOnly = false,
  showPatientName,
  queueBarRef,
  showQueueBar,
}) => {
  const cubicleList = useMemo(() => {
    if (isArray(allData.cubicles)) {
      return _.map(allData.cubicles, (item) => {
        const position = JSON.parse(item.position);
        let remarks = item.remark;

        if (item.cubType === 'Memo') {
          const blocksFromHtml = htmlToDraft(item?.remark ?? '');
          const { contentBlocks, entityMap } = blocksFromHtml;
          const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
          remarks = EditorState.createWithContent(contentState);
        }

        return {
          id: item.cubicleId,
          x: position.X,
          y: position.Y,
          width: item.cubWidth,
          height: item.cubHeight,
          data: {
            ...item,
            name: item.name,
            gender: item.gender,
            type: item.cubType,
            status: item.status,
            remarks: remarks,
            color: item.cubColour,
            remarkColor: item.reColour,
            bedArrangement: {
              column: item.cubColums,
              row: item.cubRows,
            },
            id: item.cubicleId,
            beds: _.map(generateGrid(item.cubRows, item.cubColums), (gridItem) => {
              const bed = _.find(
                item.bedNos,
                (bedItem) =>
                  Number(bedItem.bedRow) === Number(gridItem.row) &&
                  Number(bedItem.bedColumn) === Number(gridItem.column)
              );
              if (bed) {
                return {
                  ...gridItem,
                  data: bed,
                };
              } else {
                return gridItem;
              }
            }),
            edit: false,
            width: item.cubWidth,
            height: item.cubHeight,
            header: item.header ?? 'show',
          },
        };
      });
    } else {
      return [];
    }
  }, [allData]);
  const isMutipleWardCode = useMemo(() => allData?.multipWardCd, [allData?.multipWardCd]);
  const checkLogin = useCheckLogin();
  const sso = useSelector((state) => state.ssoReducer);
  const { isCubicleV2 } = useSelector((state) => state.bedStatusView);
  const showMemo = useBsMemo();
  const [selectedWard, setSelectedWard] = useState('');

  const floorMemo = useMemo(() => {
    let width = 0;
    let height = 0;
    let top = 0;
    let left = 0;

    _.forEach(cubicleList, (item) => {
      if (Number(item.x) + Number(item.width) > Number(width)) {
        width = Number(item.x) + Number(item.width);
      }

      if (Number(item.y) + Number(item.height) > Number(height)) {
        height = Number(item.y) + Number(item.height);
      }

      if (Number(item.y) < 0 && Number(item.y) < Number(top)) {
        top = -Number(item.y);
      }

      if (Number(item.x) < 0 && Number(item.x) < Number(left)) {
        left = -Number(item.x);
      }
    });

    return {
      width,
      height: isCubicleV2 ? height : height + 35,
      top,
      left,
    };
  }, [cubicleList, showPatientName, isCubicleV2]);

  const isEmptyFloorPlan = useMemo(
    () => Array.isArray(allData.cubicles) && allData.cubicles?.length === 0,
    [allData.cubicles]
  );
  const [floorPlanWidth, setfloorPlanWidth] = useState(0);
  const [detailOpen, setDetailOpen] = useState(sso ? false : true);
  const classes = useStyles({
    detailOpen,
    floorMemo,
    allData,
    showPatientName,
    isCubicleV2,
    floorPlanWidth,
    isEmptyFloorPlan,
  });
  const [editOpen, setEditOpen] = useState(false);
  const fullScreen = useSelector((state) => state.fullScreenReducer);

  // 控制 detail
  const handleOpenDetail = useCallback(() => {
    setDetailOpen((o) => !o);
  }, []);

  const updateBedStatus = useCallback(
    async (data, closePop) => {
      try {
        await bedStatusAPI.postUpdateStatus(data);
        try {
          await getFloorPlanListAPI(closePop);
          bsTransferInStatisticRef?.current?.getTransferStatistic();
        } catch {
          closePop();
        }
      } catch (e) {
        if (e.statusCode === ResponseCommCodeEnum.BED_VERSION) {
          await getFloorPlanListAPI();
          bsTransferInStatisticRef?.current?.getTransferStatistic();
          alertMsg(
            i18n.t(
              'BEDSTATUS_BED_STATUS_OF_THIS_BED_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_OR_IPAS_PLEASE_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_BED_STATUS_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
            ),
            _.uniqueId('warning_'),
            'warning'
          );
        }
      }
    },
    [i18n.t(), getFloorPlanListAPI]
  );

  const saveBedStatus = useCallback(
    async (data, closePop) => {
      try {
        await bedStatusAPI.postSaveRemark({ ...data, ccWardCd: data.wardCd });
        try {
          await getFloorPlanListAPI(closePop);
          bsTransferInStatisticRef?.current?.getTransferStatistic();
        } catch {
          closePop();
        }
      } catch (e) {
        if (e.statusCode === ResponseCommCodeEnum.BED_VERSION) {
          await getFloorPlanListAPI();
          bsTransferInStatisticRef?.current?.getTransferStatistic();
          alertMsg(
            i18n.t(
              'BEDSTATUS_BED_STATUS_OF_THIS_BED_WAS_CHANGED_BY_OTHER_WARDPLACE_USER_OR_IPAS_PLEASE_REFRESH_WEB_PAGE_TO_GET_THE_LATEST_BED_STATUS_INFO_AND_CHECK_IF_FURTHER_ACTION_IS_REQUIRED'
            ),
            _.uniqueId('warning_'),
            'warning'
          );
        }
      }
    },
    [i18n.t(), getFloorPlanListAPI]
  );

  const saveReturnDayCase = useCallback(
    async (data, _closePop) => {
      try {
        await bedStatusAPI.postSaveReturnDayCase(data);
        await getFloorPlanListAPI();
        bsTransferInStatisticRef?.current?.getTransferStatistic();
      } catch (error) {}
    },
    [i18n.t(), getFloorPlanListAPI]
  );

  const bedsList = useMemo(() => {
    let beds = [];

    if (allData.cubicles.length > 0) {
      beds = allData.leftBedNos;
    } else {
      beds = _.flatMap(allData.bedStatus, (item) => item.wardBeds);
    }
    const waitingBedList = _.filter(beds, (item) => handleBedsFilter(filterData, item));

    const groudBedByWarcCd = _.groupBy(waitingBedList, (bedItem) => bedItem?.ccWardCd);
    const groupBedList = _.map(Object.keys(groudBedByWarcCd), (item) => ({
      ccWardCd: item,
      bedList: groudBedByWarcCd?.[item],
    }));

    const wardOptionList = [
      { text: 'All', value: '' },
      ..._.map(groupBedList, (item) => ({
        text: item?.ccWardCd,
        value: item?.ccWardCd,
      })),
    ];

    return { waitingBedList, groupBedList, wardOptionList };
  }, [allData, filterData]);

  const handleUpdateWardStatus = useCallback(
    async (params) => {
      try {
        await (params.floorPlanId ? bedStatusAPI.updateFloorPlan(params) : bedStatusAPI.createFloorPlan(params));
        setEditOpen(false);
        getFloorPlanListAPI();
      } catch (error) {
        console.log(error);
      }
    },
    [getFloorPlanListAPI]
  );

  // 修改 Nurse station
  const handleUpdateNurseStation = useCallback(
    async (data, form, handleClose) => {
      try {
        const params = {
          cubColour: form.color,
          cubColums: data.cubColums,
          cubHeight: data.cubHeight,
          cubRows: data.cubRows,
          cubType: data.cubType,
          cubWidth: data.cubWidth,
          cubicleId: data.cubicleId,
          gender: data.gender,
          name: form.name,
          position: data.position,
          reColour: data.reColour,
          remark: data.remark,
          status: data.status,
          version: data.version,
          borderLine: form.borderLine,
        };
        await bedStatusAPI.updateCubicle(params);
        handleClose();
        getFloorPlanListAPI();
      } catch (error) {}
    },
    [getFloorPlanListAPI]
  );

  // 修改 Cubicle
  const handleUpdateCubicle = useCallback(
    async (data, form, handleClose) => {
      try {
        const params = {
          cubColour: form.color,
          cubColums: data.cubColums,
          cubHeight: data.cubHeight,
          cubRows: data.cubRows,
          cubType: form.type,
          cubWidth: data.cubWidth,
          cubicleId: data.cubicleId,
          gender: form.gender,
          name: data.name,
          position: data.position,
          reColour: form.remarkColor.value,
          remark: data.type === 'Memo' ? handleDeleteEmbedBug(form.remarks) : form.remarks,
          status: form.status,
          version: data.version,
          borderLine: form.borderLine,
          header: data.header,
        };
        await bedStatusAPI.updateCubicle(params);
        handleClose();
        getFloorPlanListAPI();
      } catch (error) {}
    },
    [getFloorPlanListAPI]
  );

  const getCubicleStyle = useCallback(
    (item) => {
      const cubicleWidth = item?.width;
      const cubicleLeft = item.x;

      return {
        position: 'absolute',
        top: `${item.y + floorMemo.top}px`,
        left: `${cubicleLeft + floorMemo.left}px`,
        width: `${cubicleWidth}px`,
        height: `${item.height}px`,
      };
    },
    [showPatientName]
  );

  // 打开或关闭全屏的时候顺带打开或关闭 detail
  useEffect(() => {
    if (fullScreen) {
      setDetailOpen(false);
    } else {
      setDetailOpen(sso ? false : true);
    }
  }, [fullScreen, sso]);

  const BedIconComponent = useMemo(() => {
    return isCubicleV2 ? RevampBedStatusIcon : BedStatusIcon;
  }, [isCubicleV2]);

  useEffect(() => {
    // 计算并更新宽度
    const calculatedWidth = () => {
      try {
        // 页面宽度
        const pageWidth = document.documentElement?.clientWidth || window.innerWidth || 0;
        // queue bar的宽度
        const queueBarWidth = queueBarRef?.current ? queueBarRef.current.getBoundingClientRect().width : 0;
        // 再减去gap / padding等的宽度
        let reducedWidth = 0;
        if (readOnly) {
          reducedWidth = 4;
        } else {
          reducedWidth = isCubicleV2 ? 8 : 10;
        }
        const availableWidth = Math.round(pageWidth - queueBarWidth - calculateRemToPx(reducedWidth));
        setfloorPlanWidth(availableWidth);
      } catch {
        setfloorPlanWidth(0);
      }
    };
    calculatedWidth();
    window.addEventListener('resize', calculatedWidth);

    return () => {
      window.removeEventListener('resize', calculatedWidth);
    };
  }, [queueBarRef, showQueueBar, isCubicleV2]);

  return (
    <Box>
      {/* ward */}
      <Box display="flex" alignItems="center" gridGap="0.625rem" mb="0.25rem">
        <div className={classes.wardName}>{allData.wardLocation}</div>
        <ButtonBase
          className={classes.moreBtn}
          onClick={() => {
            if (checkLogin()) {
              setEditOpen(true);
            }
          }}
          disabled={readOnly}
        >
          <MoreIcon className={classes.moreIcon} />
        </ButtonBase>
        {allData.status && (
          <div className={classes.wardStatus}>
            <div className={classes.wardStatusText}>{allData.status}</div>
          </div>
        )}
        {allData.remark && (
          <div className={classes.tipBar}>
            <div className={classes.tips}>{allData.remark}</div>
          </div>
        )}
      </Box>

      {/* floor plan */}
      <Box
        display="inline-flex"
        flexDirection="column"
        border="solid 1px #e4e7eb"
        bgcolor="#fff"
        borderRadius="12px 12px 12px 12px"
        padding="0.571rem 0.857rem 2.438rem"
        width={isEmptyFloorPlan ? floorPlanWidth : 'unset'}
      >
        <Box borderBottom="solid 1px #ddd" pb="1rem">
          {/* ward status */}
          {detailOpen && (
            <Box className={classes.floorPlanStatusBox}>
              <Box display="flex" flexDirection="column" gridGap="0.75rem">
                <Box display="flex" gridGap="0.625rem">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="2.5rem"
                    height="2.5rem"
                    bgcolor="#44d7b6"
                    borderRadius="5px"
                  >
                    <BedAvailableIcon className={classes.bedAvailableIcon} />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <div className={classes.statusBoldText} style={{ lineHeight: '1.5rem' }}>
                      {allData.statistics.rateInfo.available}
                    </div>
                  </Box>
                </Box>

                {allData?.statistics?.rateInfo?.wardCds?.length > 0 && (
                  <Box>
                    {allData?.statistics?.rateInfo?.wardCds?.map((item) => (
                      <Text
                        fontSize="1rem"
                        color="#2E4469"
                        whiteSpace="nowrap"
                        key={item.wardCd}
                      >{`${item.wardCd} : ${item.available}`}</Text>
                    ))}
                  </Box>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.5rem">
                <Box display="flex" gridGap="0.625rem">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="2.5rem"
                    height="2.5rem"
                    bgcolor="#ff655c"
                    borderRadius="5px"
                  >
                    <OccupiedIcon className={classes.bedAvailableIcon} />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <div className={classes.statusBoldText} style={{ lineHeight: '1.5rem' }}>
                      {`${
                        allData.statistics.rateInfo.free === 0
                          ? 0
                          : Math.floor((allData.statistics.rateInfo.used / allData.statistics.rateInfo.free) * 100)
                      }%`}
                    </div>
                    <div
                      className={classes.statusTextData}
                    >{`${allData.statistics.rateInfo.used}/${allData.statistics.rateInfo.free}`}</div>
                  </Box>
                </Box>

                {allData?.statistics?.rateInfo?.wardCds?.length > 0 && (
                  <Box>
                    {allData?.statistics?.rateInfo?.wardCds?.map((item) => (
                      <Text fontSize="1rem" color="#2E4469" whiteSpace="nowrap" key={item.wardCd}>{`${item.wardCd} : ${
                        item.used
                      }/${item.free} (${item.free === 0 ? 0 : Math.floor((item.used / item.free) * 100)}%)`}</Text>
                    ))}
                  </Box>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.125rem" minWidth="6rem">
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Vacant:</div>
                  <div className={classes.statusTextData}>{allData.statistics.vacant}</div>
                </Box>
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Occupied:</div>
                  <div className={classes.statusTextData}>{allData.statistics.occupied}</div>
                </Box>
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Reserved:</div>
                  <div className={classes.statusTextData}>{allData.statistics.reserved}</div>
                </Box>
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Cleansing:</div>
                  <div className={classes.statusTextData}>{allData.statistics.cleansing}</div>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.125rem" minWidth="6.5rem">
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Frozen:</div>
                  <div className={classes.statusTextData}>{allData.statistics.frozen}</div>
                </Box>
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Home Leave:</div>
                  <div className={classes.statusTextData}>{allData.statistics.homeLeave}</div>
                </Box>
                <Box width="100%" display="flex" justifyContent="space-between">
                  <div className={classes.statusText}>Missing:</div>
                  <div className={classes.statusTextData}>{allData.statistics.missing}</div>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gridGap="0.625rem" minWidth="9rem" maxWidth="16rem">
                <Box display="flex" flexDirection="column" gridGap="0.188rem">
                  <Box display="flex" gridGap="0.25rem">
                    <div className={classes.letterIcon}>
                      <div className={classes.letterText}>D</div>
                    </div>
                    <div className={classes.statusText}>
                      Discharging:
                      <span className={classes.statusTextData} style={{ marginLeft: '0.25rem' }}>
                        {allData.statistics.discharging.length}
                      </span>
                    </div>
                  </Box>
                  <div className={classes.statusTextData} style={{ whiteSpace: 'normal' }}>
                    {handleSort(allData.statistics.discharging)}
                  </div>
                </Box>
                <Box display="flex" flexDirection="column" gridGap="0.188rem">
                  <Box display="flex" gridGap="0.25rem">
                    <div className={classes.letterIcon} style={{ backgroundColor: '#7d86a9' }}>
                      <div className={classes.letterText} style={{ zoom: '0.6' }}>
                        TO
                      </div>
                    </div>
                    <div className={classes.statusText}>
                      T/O:
                      <span className={classes.statusTextData} style={{ marginLeft: '0.25rem' }}>
                        {allData.statistics['T/O'].length}
                      </span>
                    </div>
                  </Box>
                  <div className={classes.statusTextData} style={{ whiteSpace: 'normal' }}>
                    {handleSort(allData.statistics['T/O'])}
                  </div>
                </Box>
              </Box>
              <div className={classes.border} />
              <Box display="flex" flexDirection="column" gridGap="0.25rem" minWidth="min-content">
                <Box display="flex" alignItems="center" gridGap="0.188rem">
                  <img className={classes.mdroIcon} src={bs_mdro_confirmed} alt="mdro icon" />
                  <div className={classes.statusBoldText} style={{ fontSize: '1rem' }}>
                    Confirmed MDRO
                  </div>
                </Box>
                {_.map(allData?.statistics?.confirmedMdro, (item, key) => (
                  <div className={classes.statusTextOpacity} key={key}>
                    {`${key}: `}
                    <span className={classes.statusTextData}>{handleAttentionSort(item).join(', ')}</span>
                  </div>
                ))}

                <Box display="flex" alignItems="center" gridGap="0.188rem" mt="0.25rem">
                  <img className={classes.mdroIcon} src={bs_mdro_contact} alt="contact mdro icon" />
                  <div className={classes.statusBoldText} style={{ fontSize: '1rem' }}>
                    Contact MDRO
                  </div>
                </Box>
                {_.map(allData?.statistics?.contactMdro, (item, key) => (
                  <div className={classes.statusTextOpacity} key={key}>
                    {`${key}: `}
                    <span className={classes.statusTextData}>{handleAttentionSort(item).join(', ')}</span>
                  </div>
                ))}

                <Box display="flex" alignItems="center" gridGap="0.188rem" mt="0.25rem">
                  <CmsAlertStandardIcon />
                  <div className={classes.statusBoldText} style={{ fontSize: '1rem' }}>
                    CMS Alert
                  </div>
                </Box>
                <span className={classes.statusTextData}>
                  {isNotEmptyArray(allData?.statistics?.cmsAlertMdro) ? allData.statistics.cmsAlertMdro.join(', ') : ''}
                </span>
              </Box>
              <div className={classes.border} />
              <Box display="flex" flexDirection="column" gridGap="0.25rem" minWidth="9rem">
                <Box display="flex" gridGap="0.188rem">
                  <Attention />
                  <div className={classes.statusBoldText} style={{ fontSize: '1rem' }}>
                    Attention
                  </div>
                </Box>
                <Box display="flex" flexDirection="column" gridGap="0.25rem">
                  <div className={classes.statusTextOpacity}>
                    {`Fall Risk: `}
                    <span className={classes.statusTextData}>
                      {handleAttentionSort(allData.statistics.attention['Fall Risk']).join(', ')}
                    </span>
                  </div>
                  <div className={classes.statusTextOpacity}>
                    {`Fragile Handling: `}
                    <span className={classes.statusTextData}>
                      {handleAttentionSort(allData.statistics.attention['Fragile Handling']).join(', ')}
                    </span>
                  </div>
                  <div className={classes.statusTextOpacity}>
                    {`Missing: `}
                    <span className={classes.statusTextData}>
                      {handleAttentionSort(allData.statistics.attention['Missing']).join(', ')}
                    </span>
                  </div>
                </Box>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                gridGap="0.25rem"
                minWidth="9rem"
                style={{ marginTop: '1.5rem' }}
              >
                <div className={classes.statusTextOpacity}>
                  {`Physical Restraint: `}
                  <span className={classes.statusTextData}>
                    {handleAttentionSort(allData.statistics.attention['Physical Restraint']).join(', ')}
                  </span>
                </div>
                <div className={classes.statusTextOpacity}>
                  {`Pressure injury: `}
                  <span className={classes.statusTextData}>
                    {handleAttentionSort(allData.statistics.attention['Pressure injury']).join(', ')}
                  </span>
                </div>
                <div className={classes.statusTextOpacity}>
                  {`SP: `}
                  <span className={classes.statusTextData}>
                    {handleAttentionSort(allData.statistics.attention['Suicidal Precaution']).join(', ')}
                  </span>
                </div>
                {/* <div className={classes.statusTextOpacity}>
                  Additional remark: {allData.statistics.attention['Additional remark'].join(', ')}
                </div> */}
              </Box>
              <div className={classes.border} />
              <Box display="flex" flexDirection="column" minWidth="min-content" height="100%" gridGap="0.75rem">
                <Box display="flex" alignItems="center" gridGap="0.5rem">
                  <Box>
                    <Box display="flex" gridGap="0.625rem" alignItems="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="2.5rem"
                        height="2.5rem"
                        bgcolor="#00a8f9"
                        borderRadius="5px"
                      >
                        <AdmIcon className={classes.bedAvailableIcon} />
                      </Box>
                      <Box>
                        <div className={classes.statusText}>Adm.</div>
                        <div className={classes.statusBoldText}>{allData.statistics.ADM}</div>
                      </Box>
                    </Box>
                    <Box display="flex" gridGap="0.625rem" alignItems="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="2.5rem"
                        height="2.5rem"
                        bgcolor="#997bff"
                        borderRadius="5px"
                      >
                        <DischgIcon className={classes.bedAvailableIcon} />
                      </Box>
                      <Box display="flex" flexDirection="column">
                        <div className={classes.statusText}>Dischg.</div>
                        <div className={classes.statusBoldText}>{allData.statistics.DIS}</div>
                      </Box>
                    </Box>
                  </Box>
                  <div className={classes.admText}>from 09:00</div>
                </Box>
                <div className={classes.queuingPatient}>
                  <PatientIcon className={classes.patientIcon} /> Queue: {allData.statistics.queuing}
                </div>
              </Box>
            </Box>
          )}

          {/* detail bar */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb="0.9rem">
            <ButtonBase className={classes.detailBtn} onClick={handleOpenDetail}>
              Statistic
              {detailOpen ? <DropUpIcon className={classes.dropIcon} /> : <DropDownIcon className={classes.dropIcon} />}
            </ButtonBase>
            <Box width="100%" height="1px" bgcolor="#ddd" />
          </Box>

          {/* Floor Plan */}
          {!isEmptyFloorPlan ? (
            <div className={classes.floorRoot}>
              {cubicleList
                .filter((item) => {
                  if (item.data.type === 'Memo') {
                    return showMemo;
                  } else {
                    return true;
                  }
                })
                .map((item) => (
                  <Suspense fallback={<div />} key={item.id}>
                    <Cubicle
                      key={item.id}
                      style={getCubicleStyle(item)}
                      {...item.data}
                      getFloorPlanListAPI={getFloorPlanListAPI}
                      handleUpdateNurseStation={handleUpdateNurseStation}
                      handleUpdateCubicle={handleUpdateCubicle}
                      setPopup={setPopup}
                      filterData={filterData}
                      readOnly={readOnly}
                      hospCode={allData?.hospCode}
                      wardLocation={allData?.wardLocation}
                      showPatientName={showPatientName}
                      isMutipleWardCode={isMutipleWardCode}
                    />
                  </Suspense>
                ))}
            </div>
          ) : (
            <Box>
              <Box display="flex" justifyContent="flex-end" pr="2rem">
                <AutoCompleteSelect
                  id="cubicle_view2_bed_filter_select"
                  itemList={bedsList?.wardOptionList}
                  width="9.375rem"
                  height="2.938rem"
                  value={selectedWard}
                  onChange={(val) => setSelectedWard(val)}
                />
              </Box>
              <Box mt="1rem" display="flex" flexDirection="column" gridGap="1.285rem">
                {_.map(bedsList?.groupBedList, (item, groupIndex) => {
                  return !selectedWard || selectedWard === item?.ccWardCd ? (
                    <Box key={getArrayRandomKey(groupIndex, `wards-${item?.ccWardCd}`)}>
                      <Box fontSize="1.428rem" fontWeight="bold">
                        {item?.ccWardCd}
                      </Box>
                      <div className={classes.bedsRoot}>
                        {_.map(item?.bedList, (bedItem, bedIndex) => (
                          <Box
                            key={getArrayRandomKey(bedIndex, `watingBeds-${bedItem.bedStatusId}`)}
                            width="5rem"
                            height="4.688rem"
                            pt={isCubicleV2 ? '0' : '1.563rem'}
                          >
                            <BedIconComponent
                              isAbsolute={true}
                              allData={bedItem}
                              status={bedItem.status}
                              updateBedStatus={updateBedStatus}
                              bedNo={bedItem.bedNo}
                              gender={bedItem.patGender}
                              saveBedStatus={saveBedStatus}
                              saveReturnDayCase={saveReturnDayCase}
                              ward={bedItem.ccWardCd}
                              reload={getFloorPlanListAPI}
                              disabled={readOnly}
                              isMutipleWardCode={isMutipleWardCode}
                              showPatientName={showPatientName}
                            />
                          </Box>
                        ))}
                      </div>
                    </Box>
                  ) : null;
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Beds */}
        {!isEmptyFloorPlan && (
          <Box mt="1rem" width={floorPlanWidth}>
            <Box display="flex" justifyContent="flex-end" pr="2rem">
              <AutoCompleteSelect
                id="cubicle_view2_bed_filter_select"
                itemList={bedsList?.wardOptionList}
                width="9.375rem"
                height="2.938rem"
                value={selectedWard}
                onChange={(val) => setSelectedWard(val)}
              />
            </Box>
            <Box mt="1rem" display="flex" flexDirection="column" gridGap="1.285rem">
              {_.map(bedsList?.groupBedList, (item, groupIndex) => {
                return !selectedWard || selectedWard === item?.ccWardCd ? (
                  <Box key={getArrayRandomKey(groupIndex, `ward-${item?.ccWardCd}`)}>
                    <Box fontSize="1.428rem" fontWeight="bold">
                      {item?.ccWardCd}
                    </Box>
                    <Box display="flex" gridGap={isCubicleV2 ? '0.857rem' : '1.428rem'} flexWrap="wrap" mt="0.3rem">
                      {_.map(item?.bedList, (bedItem, bedIndex) => (
                        <Box
                          key={getArrayRandomKey(bedIndex, `watingBed-${bedItem.bedStatusId}`)}
                          width="5rem"
                          height="4.688rem"
                          pt={isCubicleV2 ? '0' : '1.563rem'}
                        >
                          <BedIconComponent
                            isAbsolute={true}
                            allData={bedItem}
                            status={bedItem.status}
                            updateBedStatus={updateBedStatus}
                            bedNo={bedItem.bedNo}
                            gender={bedItem.patGender}
                            saveBedStatus={saveBedStatus}
                            saveReturnDayCase={saveReturnDayCase}
                            ward={bedItem.ccWardCd}
                            reload={getFloorPlanListAPI}
                            disabled={readOnly}
                            isMutipleWardCode={isMutipleWardCode}
                            showPatientName={showPatientName}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null;
              })}
            </Box>
          </Box>
        )}
      </Box>

      {editOpen && (
        <UpdateWardStatus
          data={allData}
          handleClose={() => setEditOpen(false)}
          handleUpdateWardStatus={handleUpdateWardStatus}
          setPopup={setPopup}
        />
      )}
    </Box>
  );
};

export default FloorPlan;
